import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { fetchMobLoads, saveMobLoads, type MarginView, type MobLoadRow } from '../bidService';
import { getBidUuid } from '../bidStore';
import { money, useFinancialVisible } from '../financialDisplay';
import { useRateCardOptions } from '../Admin/useRateCardOptions';
import {
  DEMOB_LINE_PREFIX,
  MOB_LINE_PREFIX,
  MOB_LOADS_LINE_PREFIX,
  newId,
  type BidLineItem,
  type BidPhase,
  type CalcLineUpsert,
} from './bidWorkbook';

/**
 * Mob/Demob load-count matrix (gap 3.11) + load pricing (gap 4.1, answered 2026-08-07:
 * "build the automated calculation").
 *
 * The counts are quantities, so the matrix renders for every caller (no money to hide) and the
 * server guards the write. The PRICING half is money: it renders only once the resolved margins
 * arrive (the server refuses them to a blind caller), and it applies as **visible lines** the
 * estimator confirms — never a hidden multiplier (the endorsed 4.1 frame). Apply is refused
 * while the matrix has unsaved edits: the priced lines must trace to recorded counts.
 *
 * Applied as the MOBE-DEMOBE sheet's own TWO blocks (plan mob-demob-sheet-parity, Build M):
 * a Mobilization line and a Demobilization line, each total loads × `loadRate`, in ONE persist.
 * A line still wearing the legacy combined signature is adopted into the Mobilization slot, so
 * no bid keeps a stale single line beside the pair.
 *
 * Explicit save (the MarginPanel idiom), not per-keystroke commits: the PUT is a replace-all,
 * and half-typed rows must not race each other onto the wire.
 */
interface MobLoadsCardProps {
  bidId: string;
  /** The live workbook phases — where the applied pair lives, and the Apply target list. */
  phases: BidPhase[];
  /** Fixed Apply target — set by the mob PHASE EDITOR mount (Build M), which pins new lines to
   * its own phase and hides the target picker. Absent on the summary-fallback mount. */
  phaseId?: string;
  /** Resolved margin view (fetched by the page; null while loading or for a blind caller —
   * either way there is no rate to price with, so the pricing half stays hidden). */
  margins: MarginView | null;
  /** Status edit-lock: the matrix is the record the loads lines priced from — read-only, and
   * Apply is withheld (the server refuses both writes with a 409 regardless). */
  locked?: boolean;
  /** Land the calculator's pair — appends and in-place updates batched into ONE persist (the
   * welding Apply's discipline: a button click is not typing, and never two saves), with
   * `removeItemIds` sweeping superseded legacy combined lines in the same write. */
  onApplyLines: (upserts: CalcLineUpsert[], removeItemIds?: string[]) => void;
}

const emptyRow = (): MobLoadRow => ({ label: '', heavyLoads: 0, legalLoads: 0, pickupLoads: 0 });

const MobLoadsCard = ({ bidId, phases, phaseId, margins, locked = false, onApplyLines }: MobLoadsCardProps): JSX.Element | null => {
  const [rows, setRows] = useState<MobLoadRow[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A FAILED load must never render as an empty matrix: "empty" invites a Save whose
  // replace-all would delete the rows the server really holds (review — the same wipe the
  // workbook editor's own failed-load gate exists to prevent). Distinct state + retry instead.
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  // '' = no explicit choice yet — the default target (a phase named like "Mob…", else the
  // first) applies until the estimator picks one.
  const [targetPhaseId, setTargetPhaseId] = useState('');
  // Subscribes this card to the visibility flip — money() reads module state, so without a
  // subscriber an entitled caller's figures would stay redacted until an unrelated re-render.
  // The value also gates the pricing strip: a mid-session revocation must drop the strip even
  // while the already-fetched margins linger in the parent's state (review).
  const financialVisible = useFinancialVisible();
  // The bid's FROZEN equipment catalog for the type picker (plan walkthrough-and-loads-picker,
  // Build P) — the LineItemTable's exact source discipline. The stored value stays the LABEL
  // (bid_mob_loads has no catalog FK, and the sheet's own rows include non-equipment types like
  // Warehousing), so a blind caller's empty option list degrades to today's free text.
  const equipmentOptions = useRateCardOptions(bidId, 'equipment');
  // The frozen card's DISTINCT categories (Build C, plan mob-loads-categories-and-type-slots):
  // the matrix counts loads BY TYPE, and the MSA's Category axis IS that type — so "Dozers"
  // should be a pick, not Custom…. Blank-guarded (category is nullable/strippable; an empty
  // one must not mint a pick) and alphabetized. `cat:` namespaces the option values — no
  // catalog uuid can collide with it.
  const categories = [...new Set(
    equipmentOptions.map((o) => (o.category ?? '').trim()).filter(Boolean),
  )].sort((a, b) => a.localeCompare(b));

  const uuid = getBidUuid(bidId);

  useEffect(() => {
    let cancelled = false;
    if (!uuid) return undefined;
    setLoadFailed(false);
    fetchMobLoads(uuid)
      .then((loaded) => { if (!cancelled) setRows(loaded); })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; };
  }, [uuid, loadAttempt]);

  if (loadFailed) {
    return (
      <div className="bid-cover bid-mob-loads">
        <h2 className="bid-panel-title">Mob / Demob Loads</h2>
        <p className="bid-panel-subtitle">
          Could not load the load matrix. Editing is disabled so a save cannot overwrite what is
          already recorded.
        </p>
        <button className="bid-add-row" onClick={() => setLoadAttempt((n) => n + 1)}>
          Retry
        </button>
      </div>
    );
  }

  if (rows === null) return null;

  const patchRow = (i: number, patch: Partial<MobLoadRow>): void => {
    setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const count = (raw: string): number | null => {
    // Refuse, never rewrite: '' means 0 here (a load row exists to be counted), but a negative
    // or fractional count is ignored rather than clamped into a fact nobody stated.
    if (raw === '') return 0;
    const n = Number(raw);
    return Number.isInteger(n) && n >= 0 ? n : null;
  };

  const save = async (): Promise<void> => {
    if (!uuid || saving) return;
    // Vendor-less rows with counts REFUSE the save (the invoice partial-row doctrine) — a row
    // dropped silently would destroy typed counts; a fully-empty row is just noise and drops.
    const labelless = rows.some(
      (r) => !r.label.trim() && (r.heavyLoads > 0 || r.legalLoads > 0 || r.pickupLoads > 0),
    );
    if (labelless) {
      setError('Every row with counts needs an equipment type.');
      return;
    }
    const toSave = rows.filter((r) => r.label.trim());
    setSaving(true);
    setError(null);
    try {
      const saved = await saveMobLoads(uuid, toSave);
      setRows(saved);
      setDirty(false);
    } catch (err) {
      const status = (err as { status?: number })?.status;
      const message = (err as { message?: string })?.message;
      setError(status === 403
        ? 'Saving the load matrix needs bid-editing permissions.'
        // 409 = the status edit-lock (a cross-tab transition can race this card) — the server
        // names the status and the way back; show it verbatim.
        : status === 409 && message
          ? message
          : 'Could not save the load matrix. Your rows are still here — try again.');
    } finally {
      setSaving(false);
    }
  };

  const totals = rows.reduce(
    (acc, r) => ({
      heavy: acc.heavy + r.heavyLoads, legal: acc.legal + r.legalLoads,
      pickup: acc.pickup + r.pickupLoads,
    }),
    { heavy: 0, legal: 0, pickup: 0 },
  );

  // ---- load pricing (gap 4.1) ----------------------------------------------
  const totalLoads = totals.heavy + totals.legal + totals.pickup;
  const rateRaw = margins?.resolved?.effective?.load_rate;
  const parsedRate = rateRaw == null ? NaN : Number(rateRaw);
  // A configured 0 IS a value (the company's own statement that freight is free) — only
  // absence refuses. No zero-shadow exists for this parameter.
  const loadRate = Number.isFinite(parsedRate) && parsedRate >= 0 ? parsedRate : null;
  // OWNERSHIP is prefix + the calculator's own suffix shape (plan review M4): unlike the
  // machine-only legacy prefix, "Mobilization — " is natural English, and a hand-typed
  // "Mobilization — crane permits" must never be adopted and clobbered. The calculator owns its
  // emitted counts shape and the demo seed's exact "Legal Loads" spelling — nothing else (the
  // seed writes no other load class, so "Heavy/Light Loads" stay hand territory; batch L4).
  const OWNED_SUFFIX = /^(\d+ heavy, \d+ legal, \d+ pickup|Legal Loads)$/;
  // ALL wearers per prefix (review R5): descriptions are free text, so a hand-typed twin of the
  // owned shape can sit beside the applied line. The FIRST wearer is the slot Apply updates;
  // every other wearer sweeps in the same persist — one line per prefix, the legacy-sweep
  // doctrine (H1) applied to the pair's own shape.
  const findOwned = (prefix: string): { phase: BidPhase; item: BidLineItem }[] =>
    phases.flatMap((p) => p.items
      .filter((it) => it.description.startsWith(prefix)
        && OWNED_SUFFIX.test(it.description.slice(prefix.length)))
      .map((item) => ({ phase: p, item })));
  const mobOwned = findOwned(MOB_LINE_PREFIX);
  const demobOwned = findOwned(DEMOB_LINE_PREFIX);
  const mobApplied = mobOwned[0] ?? null;
  const demobApplied = demobOwned[0] ?? null;
  // EVERY pre-split combined line (the legacy prefix is machine-only, so any wearer is ours):
  // the first is adopted into the Mobilization slot when no mob line exists; the rest are swept
  // in the same persist — no bid keeps a stale single line beside the pair (plan review H1).
  const legacyLines = phases.flatMap((p) => p.items
    .filter((it) => it.description.startsWith(MOB_LOADS_LINE_PREFIX))
    .map((item) => ({ phase: p, item })));
  const applied = mobApplied ?? demobApplied ?? legacyLines[0] ?? null;
  const defaultPhaseId = (phases.find((p) => /mob/i.test(p.name)) ?? phases[0])?.id ?? '';
  const effectiveTarget = phaseId || targetPhaseId || defaultPhaseId;

  const applyLoadsLines = (): void => {
    if (loadRate === null || dirty || saving || totalLoads === 0) return;
    // NO money in the descriptions — they survive the financial strip (bidWorkbook's rule).
    const counts = `${totals.heavy} heavy, ${totals.legal} legal, ${totals.pickup} pickup`;
    const parts = { qty: totalLoads, hrs: 1, days: 1, rate: loadRate };
    // Still wearing a signature -> still the calculator's line: update it where it lives, even
    // over a hand-edited qty. Renaming a line is how an estimator takes ownership of it.
    const mobSlot = mobApplied ?? legacyLines[0] ?? null;
    // The pair lives TOGETHER: new lines land beside the adopted one (wherever it lives — an
    // estimator who moved it chose that phase), else in this editor's phase / the picked target.
    const homePhase = mobSlot?.phase.id ?? demobApplied?.phase.id ?? effectiveTarget;
    if (!homePhase) return;
    const upserts: CalcLineUpsert[] = [
      mobSlot
        ? { phaseId: mobSlot.phase.id,
            line: { ...mobSlot.item, ...parts, description: MOB_LINE_PREFIX + counts },
            replaceItemId: mobSlot.item.id }
        : { phaseId: homePhase,
            line: { id: newId('eq'), kind: 'equipment', source: 'inhouse',
                    description: MOB_LINE_PREFIX + counts, ...parts } },
      demobApplied
        ? { phaseId: demobApplied.phase.id,
            line: { ...demobApplied.item, ...parts, description: DEMOB_LINE_PREFIX + counts },
            replaceItemId: demobApplied.item.id }
        : { phaseId: homePhase,
            line: { id: newId('eq'), kind: 'equipment', source: 'inhouse',
                    description: DEMOB_LINE_PREFIX + counts, ...parts } },
    ];
    const sweep = [
      ...mobOwned.filter((l) => l.item.id !== mobSlot?.item.id),
      ...demobOwned.filter((l) => l.item.id !== demobApplied?.item.id),
      ...legacyLines.filter((l) => l.item.id !== mobSlot?.item.id),
    ].map((l) => l.item.id);
    onApplyLines(upserts, sweep);
  };

  return (
    <div className="bid-cover bid-mob-loads">
      <h2 className="bid-panel-title">Mob / Demob Loads</h2>
      <p className="bid-panel-subtitle">
        Load counts by equipment type. Saved counts price as the sheet&rsquo;s two visible lines —
        Mobilization and Demobilization, each total loads × the load rate.
      </p>
      {/* One native fieldset locks the matrix wholesale (the phase editor's idiom). */}
      <fieldset className="bid-locked-fieldset" disabled={locked}>
      <div className="bid-table-wrap">
        <table className="bid-table bid-mob-loads-table">
          <thead>
            <tr>
              <th>Equipment type</th>
              <th className="ta-r">Heavy loads</th>
              <th className="ta-r">Legal loads</th>
              <th className="ta-r">Pickup loads</th>
              <th aria-label="Remove" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              // Index keys are safe here: rows are only appended/removed via the buttons and the
              // list is small; a stable id would need to survive the replace-all round trip.
              // eslint-disable-next-line react/no-array-index-key
              <tr key={i}>
                <td>
                  <div className="bid-mob-type-cell">
                    {/* Value DERIVED from the label (no extra state): typing custom text flips
                        this back to Custom… by itself, and picking Custom… is a no-op so it can
                        never destroy typed text. No rate is copied — loads price via loadRate,
                        never the type's own rate. aria-label stable (the LineItemTable rule).
                        Categories match FIRST (recorded decision (a): the matrix counts loads
                        by type, so when "Dozers" is both a category and an item, the type
                        reading wins — the item stays pickable from its group). */}
                    <select
                      className="bid-cell-select"
                      aria-label="Equipment type catalog"
                      // The blank guard keeps a FRESH row on Custom… even when a junk catalog
                      // row carries an empty name (diff review L2).
                      value={r.label
                        ? (categories.includes(r.label)
                          ? `cat:${r.label}`
                          : equipmentOptions.find((o) => o.label === r.label)?.id ?? '')
                        : ''}
                      onChange={(e) => {
                        if (e.target.value.startsWith('cat:')) {
                          patchRow(i, { label: e.target.value.slice(4) });
                          return;
                        }
                        const option = equipmentOptions.find((o) => o.id === e.target.value);
                        if (option) patchRow(i, { label: option.label });
                      }}
                    >
                      <option value="">Custom…</option>
                      {categories.length ? (
                        <>
                          <optgroup label="Categories">
                            {categories.map((c) => (
                              <option key={`cat:${c}`} value={`cat:${c}`}>{c}</option>
                            ))}
                          </optgroup>
                          <optgroup label="Items">
                            {equipmentOptions.map((opt) => (
                              <option key={opt.id} value={opt.id}>{opt.label}</option>
                            ))}
                          </optgroup>
                        </>
                      ) : (
                        // A category-less card keeps today's flat list — no empty group chrome.
                        equipmentOptions.map((opt) => (
                          <option key={opt.id} value={opt.id}>{opt.label}</option>
                        ))
                      )}
                    </select>
                    <input
                      className="bid-input"
                      aria-label="Equipment type"
                      value={r.label}
                      placeholder="e.g. Dozers"
                      onChange={(e) => patchRow(i, { label: e.target.value })}
                    />
                  </div>
                </td>
                {([['heavyLoads', 'Heavy loads'], ['legalLoads', 'Legal loads'],
                  ['pickupLoads', 'Pickup loads']] as const).map(([field, label]) => (
                  <td key={field} className="ta-r">
                    <input
                      type="number"
                      min={0}
                      className="bid-input ta-r"
                      aria-label={label}
                      value={r[field]}
                      onChange={(e) => {
                        const n = count(e.target.value);
                        if (n !== null) patchRow(i, { [field]: n } as Partial<MobLoadRow>);
                      }}
                    />
                  </td>
                ))}
                <td>
                  <button
                    className="bid-phase-delete"
                    aria-label={`Remove ${r.label || 'row'}`}
                    onClick={() => { setRows(rows.filter((_, j) => j !== i)); setDirty(true); }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="bid-table-empty">No loads recorded yet.</td></tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td className="bold">Total</td>
                <td className="ta-r bold">{totals.heavy}</td>
                <td className="ta-r bold">{totals.legal}</td>
                <td className="ta-r bold">{totals.pickup}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {/* The pricing half: only once the resolved margins arrive — a blind caller's server
          response never carries them, so this block simply does not exist for them. */}
      {financialVisible && margins !== null && (
        <div className="bid-calc-pricing">
          <h3 className="bid-calc-pricing-title">Load pricing</h3>
          {loadRate === null ? (
            <p className="bid-calc-pricing-note">
              Load pricing needs a rate — set “Load $” in Margins below, or the company
              default <code>loadRate</code> in the bid admin catalogs.
            </p>
          ) : totalLoads === 0 ? (
            <p className="bid-calc-pricing-note">No loads counted yet — nothing to price.</p>
          ) : (
            <>
              <p className="bid-calc-pricing-draft">
                {totalLoads} loads × {money(loadRate)} / load ={' '}
                <strong>{money(totalLoads * loadRate)}</strong> each way
                {' — '}{money(2 * totalLoads * loadRate)} mob + demob
              </p>
              {locked ? (
                <p className="bid-calc-pricing-note">
                  Pricing is locked while the bid is a record — the applied lines, if any, stay
                  as submitted.
                </p>
              ) : phases.length === 0 ? (
                <p className="bid-calc-pricing-note">
                  Add a phase first — the priced lines live in a phase.
                </p>
              ) : (
                <div className="bid-calc-pricing-actions">
                  {applied ? (
                    <span className="bid-calc-pricing-note">
                      Applied in “{applied.phase.name || 'Untitled phase'}”. Renaming a line
                      hands it over to hand editing.
                    </span>
                  ) : phaseId ? null : (
                    <select
                      className="bid-input"
                      aria-label="Phase for the loads lines"
                      value={effectiveTarget}
                      onChange={(e) => setTargetPhaseId(e.target.value)}
                    >
                      {phases.map((p) => (
                        <option key={p.id} value={p.id}>{p.name || 'Untitled phase'}</option>
                      ))}
                    </select>
                  )}
                  <button
                    className="bid-add-row"
                    onClick={applyLoadsLines}
                    disabled={dirty || saving}
                  >
                    {applied ? 'Update applied lines' : 'Apply as lines'}
                  </button>
                  {dirty && (
                    <span className="bid-calc-pricing-note">
                      Save your counts first — the lines price recorded counts.
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {error && <p className="bid-mob-loads-error">{error}</p>}
      {!locked && (
        <div className="bid-mob-loads-actions">
          <button className="bid-add-row" onClick={() => { setRows([...rows, emptyRow()]); setDirty(true); }}>
            <FontAwesomeIcon icon={faPlus} /> Add row
          </button>
          <button className="bid-detail-walk-btn" onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save loads'}
          </button>
        </div>
      )}
      </fieldset>
    </div>
  );
};

export default MobLoadsCard;
